import React, { useContext, useState } from 'react';
import TableComponent, {
  IColumnProps,
} from '../../../components/TableComponent';
import {
  IDefenseDeviceFilterProps,
  IDefenseDeviceProps,
} from '../../../services/Defense/Devices/types';
import ButtonActionsComponent from '../../../components/ButtonActionsComponent';
import { RemoveRedEye, Sync } from '@mui/icons-material';
import {
  useGetDefenseDeviceById,
  useMutationSynchronizeDevices,
} from '../../../services/Defense/Devices/hooks/useMutations';
import { Box } from '@mui/material';
import IsOnlineComponent from '../../../components/IsOnlineComponent';
import DefenseDeviceDetailsModal from './_components/DefenseDeviceDetailsModal';
import { IPaginationParams } from '../../../services/types';
import {
  useAllDefenseDevicesByLicenseAsync,
  useAllDefenseDevicesByLicenseCountAsync,
} from '../../../services/Defense/Devices/hooks/useQuery';
import { AppContext } from '../../../context/AppContextProvider';
import ButtonComponent from '../../../components/ButtonComponent';
import { t } from 'i18next';

const DefenseDevicesPage = () => {
  const [filter, setFilter] = useState<IDefenseDeviceFilterProps>({
    search: '',
  });
  const [paginationParams, setPaginationParams] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [isVisibleDetailsModal, setIsVisibleDetailsModal] =
    useState<boolean>(false);
  const [selectedDevice, setSelectedDevice] = useState<
    IDefenseDeviceProps | undefined
  >(undefined);
  const { showSnackbar } = useContext(AppContext);
  const {
    data: defenseDevices,
    refetch: refetchAllDefenseDevices,
    isLoading: isLoadingDevices,
  } = useAllDefenseDevicesByLicenseAsync(paginationParams, filter);
  const { mutateAsync: mutateDefenseDeviceAsync } = useGetDefenseDeviceById();
  const { mutateAsync: mutateSyncDevices } = useMutationSynchronizeDevices();
  const {
    data: totalDevices,
    refetch: refetchDefenseDeviceCount,
    isLoading: isLoadingCount,
  } = useAllDefenseDevicesByLicenseCountAsync(paginationParams, filter);

  const columns: IColumnProps[] = [
    {
      id: 'isOnline',
      label: t('defensePage.deviceTable.columnTables.status'),
      width: '20',
      align: 'center',
      render: (isOnline: any) => (
        <IsOnlineComponent
          width={18}
          height={18}
          isOnline={isOnline as boolean}
        />
      ),
    },
    {
      id: 'name',
      label: t('defensePage.deviceTable.columnTables.name'),
      width: '20%',
    },
    {
      id: 'model',
      label: t('defensePage.deviceTable.columnTables.model'),
      width: '20%',
    },
    {
      id: 'manufacturer',
      label: t('defensePage.deviceTable.columnTables.brand'),
      width: '20%',
    },
    {
      id: 'defenseDeviceId',
      label: t('defensePage.deviceTable.columnTables.defenseID'),
      width: '20%',
    },
    {
      id: 'id',
      label: t('defensePage.deviceTable.columnTables.actions'),
      align: 'center',
      width: 'min',
      render: (id) => (
        <ButtonActionsComponent
          key={id}
          actionOptions={actionOptions(Number(id))}
        />
      ),
    },
  ];

  const refecthDevices = async () => {
    await refetchAllDefenseDevices();
    await refetchDefenseDeviceCount();
  };

  const actionOptions = (id: number) => {
    return [
      {
        label: 'Detalhes',
        icon: <RemoveRedEye />,
        onClick: () => {
          handleDetails(id);
        },
      },
    ];
  };

  const renderAdditionalComponents = () => {
    return (
      <ButtonComponent
        color={'primary'}
        size={'small'}
        startIcon={<Sync />}
        onClick={() => handleSynchronization()}
      >
        {t('defensePage.deviceTable.synchronize')}
      </ButtonComponent>
    );
  };

  const handleSynchronization = async () => {
    await mutateSyncDevices().then(() => {
      showSnackbar('Dispositivos sincronizados com sucesso!', 'success');
      refecthDevices();
    });
  };

  const handleDetails = (id: number) => {
    mutateDefenseDeviceAsync(id).then(setSelectedDevice);
    setIsVisibleDetailsModal(true);
  };
  return (
    <Box>
      <TableComponent
        columns={columns}
        rows={defenseDevices ?? []}
        sortByName={'name'}
        isLoading={isLoadingDevices || isLoadingCount}
        title={t('defensePage.deviceTable.title')}
        totalCount={totalDevices}
        onSubmitMainFilter={refecthDevices}
        onChangeMainFilter={(val) =>
          setFilter((prev) => ({ ...prev, search: val }))
        }
        onChangePageOrRowsPerPage={setPaginationParams}
        additionalTitleComponent={renderAdditionalComponents()}
      />
      <DefenseDeviceDetailsModal
        isOpen={isVisibleDetailsModal}
        setIsOpen={setIsVisibleDetailsModal}
        selectedDevice={selectedDevice}
        setSelectedDevice={setSelectedDevice}
      />
    </Box>
  );
};

export default DefenseDevicesPage;
